<template>
  <aside v-if="isCurrentItemOwner && !dismissed" class="mcp-context-notice" data-testid="mcp-context-notice" aria-label="Zight MCP availability">
    <Icon class="mcp-context-notice__icon" name="general.plug" size="sm" aria-hidden="true" />
    <p class="mcp-context-notice__copy">
      Transcript, chapters and on-screen text for this video are available in Claude and ChatGPT.
      <a class="mcp-context-notice__link" :href="integrationsUrl">Connect Zight MCP.</a>
    </p>
    <Button
      submit
      type="link"
      variant="black"
      icon="general.crossLight"
      class="mcp-context-notice__dismiss"
      data-testid="mcp-context-notice-dismiss"
      aria-label="Dismiss Zight MCP notice"
      @click.prevent="dismissed = true"
    />
  </aside>
</template>

<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import { computed } from 'vue';

import { Button, Icon } from '@/storybook';
import { useDebug } from '@/composables/useDebug';
import { useUser } from '@/modules/viewer/composables/useUser';

type McpContextNoticePlacement = 'ai_actions' | 'export_modal';

const props = defineProps<{
  placement: McpContextNoticePlacement;
}>();

const STORAGE_KEY = 'zight.viewer.mcp-context-notice.dismissed';
const { debug } = useDebug('mcp_context_notice');
const dismissed = useStorage(STORAGE_KEY, false, undefined, {
  onError: error => debug('Unable to access Zight MCP notice dismissal storage.', error),
});
const { isCurrentItemOwner } = useUser();

const integrationsUrl = computed(
  () => `/dashboard-new/integrations?utm_source=viewer&utm_medium=mcp_context_notice&utm_campaign=zight_mcp&utm_content=${props.placement}`
);
</script>

<style scoped>
.mcp-context-notice {
  @apply grid items-start gap-3 rounded-[10px] bg-slate-50 p-3 text-slate-600 dark:bg-zinc-800 dark:text-zinc-300;
  grid-template-columns: 20px minmax(0, 1fr) 28px;
}

.mcp-context-notice__icon {
  @apply mt-0.5 h-5 w-5;
  color: #77448d;
}

.dark .mcp-context-notice__icon {
  color: #e0adff;
}

.mcp-context-notice__copy {
  @apply text-sm leading-5;
}

.mcp-context-notice__link {
  @apply whitespace-nowrap font-semibold text-blue-500 underline-offset-2 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-blue-400 dark:focus-visible:ring-offset-zinc-800;
}

.mcp-context-notice__dismiss {
  @apply flex h-7 w-7 items-center justify-center rounded-lg border-0 bg-transparent text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-zinc-400 dark:hover:bg-zinc-700 dark:hover:text-white;
}

@media (max-width: 420px) {
  .mcp-context-notice {
    @apply gap-2 p-3;
    grid-template-columns: 20px minmax(0, 1fr) 28px;
  }
}
</style>
